import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useCategories } from '@/contexts/CategoriesContext';
import { useTranslation } from '@/hooks/useTranslation';
import { 
  getMonthData, 
  updateMonthSalary, 
  addTransaction as addTransactionToDb,
  updateTransaction as updateTransactionInDb,
  getMonthTransactions,
  deleteTransaction as deleteTransactionFromDb
} from '@/lib/firestore';
import { exportToCSV, exportToExcel } from '@/lib/export-utils';
import { AddTransactionModal } from '@/components/features/AddTransactionModal';
import { EditTransactionModal } from '@/components/features/EditTransactionModal';
import { ExportButton } from '@/components/ExportButton';
import { ArrowLeft, Plus, Trash2, Edit2, Info } from 'lucide-react';
import { formatMoney } from '@/lib/utils';
import type { Transaction } from '@/types';

export function MonthView() {
  const { month } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings(); // Get settings to access default salary
  const { base: displayCurrency, convert } = useCurrency();
  const { t } = useTranslation();
  const { getCategoryLabel } = useCategories();
  const navigate = useNavigate();
  
  const [salary, setSalary] = useState('0');
  const [salaryInitialized, setSalaryInitialized] = useState(false); // Track if salary was auto-filled
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [monthData, setMonthData] = useState({ totalGains: 0, totalExpenses: 0, savings: 0 });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const monthId = month || 'Jan';
  const accountCurrency = settings?.currency || 'USD';
  const defaultMonthlySalary = settings?.defaultMonthlySalary || 0;

  useEffect(() => {
    loadMonthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthId, user]);

  async function loadMonthData() {
    if (!user) return;
    
    setLoading(true);
    try {
      const data = await getMonthData(user.uid, currentYear, monthId);
      const trans = await getMonthTransactions(user.uid, currentYear, monthId);
      
      if (data) {
        // If month has no salary set (0) and default salary exists, use default
        // Otherwise use the saved salary
        if (data.salary === 0 && defaultMonthlySalary > 0 && !salaryInitialized) {
          setSalary(defaultMonthlySalary.toString());
          setSalaryInitialized(true);
          // Auto-save the default salary to this month
          await updateMonthSalary(user.uid, currentYear, monthId, defaultMonthlySalary);
        } else {
          setSalary(data.salary.toString());
        }
        
        setMonthData({
          totalGains: data.totalGains,
          totalExpenses: data.totalExpenses,
          savings: data.savings,
        });
      }
      
      setTransactions(trans);
    } catch (error) {
      console.error('Error loading month data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSalaryUpdate() {
    if (!user) return;
    
    try {
      await updateMonthSalary(user.uid, currentYear, monthId, parseFloat(salary) || 0);
      await loadMonthData();
    } catch (error) {
      console.error('Error updating salary:', error);
    }
  }

  async function handleAddTransaction(transaction: Omit<Transaction, 'id' | 'createdAt'>) {
    if (!user) return;
    
    await addTransactionToDb(user.uid, currentYear, monthId, transaction);
    await loadMonthData();
  }

  async function handleUpdateTransaction(transactionId: string, updates: Omit<Transaction, 'id' | 'createdAt'>) {
    if (!user) return;
    
    await updateTransactionInDb(user.uid, currentYear, monthId, transactionId, updates);
    await loadMonthData();
  }

  async function handleDeleteTransaction(transactionId: string) {
    if (!user || !window.confirm(t('transaction.deleteConfirm'))) return;
    
    await deleteTransactionFromDb(user.uid, currentYear, monthId, transactionId);
    await loadMonthData();
  }

  function handleEditClick(transaction: Transaction) {
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  }

  // Export handlers
  const handleExportCSV = () => {
    exportToCSV(transactions, `mybalance-${monthId}-${currentYear}.csv`);
  };

  const handleExportExcel = () => {
    exportToExcel(transactions, `mybalance-${monthId}-${currentYear}.xlsx`);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <div className="text-center">
          <div className="mb-4 animate-pulse text-4xl sm:text-5xl md:text-6xl">💰</div>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400">{t('common.loading')}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      {/* Fixed Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/95 flex-shrink-0">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2">
            {/* Back Button */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 sm:gap-2 rounded-lg sm:rounded-xl bg-slate-100 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-slate-700 transition-all hover:bg-slate-200 active:scale-95 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 min-h-[36px] sm:min-h-[44px]"
            >
              <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('nav.backToDashboard')}</span>
              <span className="sm:hidden">Back</span>
            </button>
            
            {/* Title */}
            <h1 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-base sm:text-xl md:text-2xl font-bold text-transparent truncate">
              {monthId} {currentYear}
            </h1>

            {/* Export Button */}
            <div className="flex-shrink-0">
              <ExportButton
                onExportCSV={handleExportCSV}
                onExportExcel={handleExportExcel}
                label={t('common.export')}
                variant="compact"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Main Content */}
      <main className="flex-1 w-full overflow-y-auto overflow-x-hidden">
        <div className="min-h-full w-full">
          <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8">
            <div className="space-y-4 sm:space-y-6 pb-8">
              
              {/* Info Banner - Shows if default salary was used */}
              {salaryInitialized && defaultMonthlySalary > 0 && (
                <div className="rounded-lg sm:rounded-xl border-2 border-green-200 bg-green-50/80 p-3 sm:p-4 dark:border-green-800 dark:bg-green-900/20 animate-slide-down">
                  <div className="flex gap-2 sm:gap-3">
                    <Info className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-green-900 dark:text-green-100">
                        {t('month.salaryAutoFilled')}
                      </p>
                      <p className="text-xs text-green-800 dark:text-green-200 mt-0.5">
                        {t('month.salaryAutoFilledMessage').replace('{amount}', formatMoney(defaultMonthlySalary, accountCurrency))}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Monthly Salary Card */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                <h2 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  💵 {t('month.monthlySalary')}
                </h2>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <input
                    type="number"
                    step="0.01"
                    value={salary}
                    onChange={(e) => setSalary(e.target.value)}
                    onBlur={handleSalaryUpdate}
                    className="flex-1 rounded-lg sm:rounded-xl bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:bg-slate-700 dark:text-slate-100 min-h-[44px]"
                    placeholder={t('month.enterSalary')}
                  />
                  <button
                    onClick={handleSalaryUpdate}
                    className="sm:w-auto rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-xl active:scale-95 min-h-[44px] whitespace-nowrap"
                  >
                    {t('common.update')}
                  </button>
                </div>
                {defaultMonthlySalary > 0 && parseFloat(salary) !== defaultMonthlySalary && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {t('month.defaultSalaryNote').replace('{amount}', formatMoney(defaultMonthlySalary, accountCurrency))}
                  </p>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {/* Salary Card */}
                <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-3 sm:p-4 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-400 mb-1">
                    {t('month.salary')}
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-blue-600 break-words">
                    {formatMoney(convert(parseFloat(salary) || 0, accountCurrency, displayCurrency), displayCurrency)}
                  </p>
                  {displayCurrency !== accountCurrency && (
                    <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
                      ({formatMoney(parseFloat(salary) || 0, accountCurrency)})
                    </p>
                  )}
                </div>
                
                {/* Total Gains Card */}
                <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-3 sm:p-4 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-400 mb-1">
                    {t('month.totalGains')}
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-emerald-600 break-words">
                    {formatMoney(convert(monthData.totalGains, accountCurrency, displayCurrency), displayCurrency)}
                  </p>
                  {displayCurrency !== accountCurrency && (
                    <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
                      ({formatMoney(monthData.totalGains, accountCurrency)})
                    </p>
                  )}
                </div>
                
                {/* Total Expenses Card */}
                <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-3 sm:p-4 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                  <p className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-400 mb-1">
                    {t('month.totalExpenses')}
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold text-red-600 break-words">
                    {formatMoney(convert(monthData.totalExpenses, accountCurrency, displayCurrency), displayCurrency)}
                  </p>
                  {displayCurrency !== accountCurrency && (
                    <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
                      ({formatMoney(monthData.totalExpenses, accountCurrency)})
                    </p>
                  )}
                </div>
                
                {/* Savings Card */}
                <div className="rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 p-3 sm:p-4 md:p-6 shadow-lg sm:shadow-xl text-white col-span-2 md:col-span-1">
                  <p className="text-[10px] sm:text-xs md:text-sm text-blue-100 mb-1">
                    {t('month.savings')}
                  </p>
                  <p className="text-base sm:text-xl md:text-2xl font-bold break-words">
                    {formatMoney(convert(monthData.savings, accountCurrency, displayCurrency), displayCurrency)}
                  </p>
                  {displayCurrency !== accountCurrency && (
                    <p className="text-[10px] sm:text-xs text-blue-100 mt-1">
                      ({formatMoney(monthData.savings, accountCurrency)})
                    </p>
                  )}
                </div>
              </div>

              {/* Add Transaction Button */}
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 sm:px-6 sm:py-4 text-sm sm:text-base font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-xl active:scale-95 flex items-center justify-center gap-2 min-h-[44px] sm:min-h-[52px]"
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                {t('month.addTransaction')}
              </button>

              {/* Transactions List */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                <h2 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  📋 {t('dashboard.transactions')} ({transactions.length})
                </h2>
                
                {transactions.length === 0 ? (
                  <div className="py-8 sm:py-12 text-center">
                    <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
                      {t('month.noTransactions')}
                    </p>
                    <p className="mt-1 text-xs sm:text-sm text-slate-400 dark:text-slate-500">
                      {t('month.clickToStart')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3 max-h-[400px] sm:max-h-[500px] md:max-h-[600px] overflow-y-auto pr-1 sm:pr-2">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg sm:rounded-xl bg-slate-50 p-3 sm:p-4 transition-colors hover:bg-slate-100 dark:bg-slate-700/50 dark:hover:bg-slate-700"
                      >
                        {/* Transaction Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <span className="text-base sm:text-lg flex-shrink-0">
                              {transaction.type === 'gain' ? '💰' : '💳'}
                              {transaction.isRecurring && <span className="ml-1 text-xs">🔄</span>}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                                {transaction.name}
                              </p>
                              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                                {getCategoryLabel(transaction.categoryId)}
                              </p>
                              {transaction.notes && (
                                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                                  {transaction.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Amount and Actions */}
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                          <div className="text-left sm:text-right">
                            <span className={`text-sm sm:text-base md:text-lg font-bold ${
                              transaction.type === 'gain' ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {transaction.type === 'gain' ? '+' : '-'}
                              {formatMoney(convert(transaction.amount, accountCurrency, displayCurrency), displayCurrency)}
                            </span>
                            {displayCurrency !== accountCurrency && (
                              <p className="text-[10px] sm:text-xs text-slate-500">
                                ({formatMoney(transaction.amount, accountCurrency)})
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleEditClick(transaction)}
                              className="rounded-lg p-2 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors text-blue-600 min-h-[36px] min-w-[36px] sm:min-h-[44px] sm:min-w-[44px] flex items-center justify-center active:scale-95"
                              title={t('common.edit')}
                            >
                              <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </button>
                            
                            <button
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              className="rounded-lg p-2 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors text-red-600 min-h-[36px] min-w-[36px] sm:min-h-[44px] sm:min-w-[44px] flex items-center justify-center active:scale-95"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <AddTransactionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddTransaction}
      />

      <EditTransactionModal
        isOpen={isEditModalOpen}
        transaction={selectedTransaction}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTransaction(null);
        }}
        onUpdate={handleUpdateTransaction}
      />
    </div>
  );
}